import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { articlesApi, wordpressApi, userSettingsApi } from "../lib/api";
import Navbar from "../components/Navbar";
import SocialPreviews from "../components/SocialPreviews";
import SocialImagesGenerator from "../components/SocialImagesGenerator";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import {
  Clock, Calendar, Tag, ChevronDown, ImagePlus, Save,
  Trash2, Send, ArrowLeft, Loader2, HelpCircle, Search,
  BookOpen, Wrench, Lightbulb, MoreVertical, Eye, Pencil, Copy, Globe, ExternalLink,
  Share2, Download, FileText, FileCode, Twitter, FileDown, Check
} from "lucide-react";const classificationConfig = {
  informativo: { label: "Informativo", color: "#3B82F6", bg: "rgba(59,130,246,0.1)", icon: BookOpen, desc: "Con ejemplos claros" },
  practico: { label: "Práctico", color: "#10B981", bg: "rgba(16,185,129,0.1)", icon: Wrench, desc: "Incluye ejercicio" },
  aplicable: { label: "Aplicable", color: "#8B5CF6", bg: "rgba(139,92,246,0.1)", icon: Lightbulb, desc: "Ejemplo de aplicación" },
};

// Strip the first leading <h1>...</h1> from generated content_html. Claude often
// includes the article title as <h1> at the top of the body — but the UI and PDF
// render the title separately, so we'd see the title twice. This removes it
// safely (only the first H1, only if it appears before any meaningful content).
const stripLeadingH1 = (html) => {
  if (!html || typeof html !== "string") return html || "";
  return html.replace(/^\s*(?:<p>\s*<\/p>\s*)*<h1\b[^>]*>[\s\S]*?<\/h1>\s*/i, "");
};

export default function ArticleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");
  const [editMeta, setEditMeta] = useState("");
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [publishingWP, setPublishingWP] = useState(false);
  const [publishingSocial, setPublishingSocial] = useState(false);
  const [wpStatus, setWpStatus] = useState(null);
  const [wpPublishStatus, setWpPublishStatus] = useState("draft");
  const [wpScheduleDate, setWpScheduleDate] = useState("");
  const [wpCategories, setWpCategories] = useState("");
  const [wpTags, setWpTags] = useState("");
  const [showWpAdvanced, setShowWpAdvanced] = useState(false);
  const [generatingSocial, setGeneratingSocial] = useState(false);
  const [socialNetworks, setSocialNetworks] = useState({ linkedin: true, facebook: true, instagram: true, x: true });
  const imageUploadRef = useRef(null);
  // Image regeneration dialog state
  const [imgDialog, setImgDialog] = useState(false);
  const [imgForm, setImgForm] = useState({
    engine: "openai",
    style: "fotorrealista",
    secondary_style: "",
    mood: "",
    custom_prompt: "",
  });
  const [imgMeta, setImgMeta] = useState(null); // { engine_used, style_used, mood_used } after gen

  useEffect(() => {
    loadArticle();
    checkWpStatus();
    loadUserNetworks();
  }, [id]);

  const loadUserNetworks = async () => {
    try {
      const res = await userSettingsApi.get();
      const nets = res.data?.social_networks;
      if (nets) {
        setSocialNetworks({
          linkedin: nets.linkedin?.enabled ?? true,
          facebook: nets.facebook?.enabled ?? true,
          instagram: nets.instagram?.enabled ?? true,
          x: nets.x?.enabled ?? true,
        });
      }
      // Pre-fill image dialog with profile defaults
      const vp = res.data?.visual_preferences || {};
      setImgForm((prev) => ({
        ...prev,
        engine: vp.default_engine || prev.engine,
        style: vp.default_style || prev.style,
        secondary_style: vp.secondary_style || "",
        mood: vp.mood || "",
        custom_prompt: "",
      }));
    } catch {}
  };

  const checkWpStatus = async () => {
    try {
      const res = await wordpressApi.status();
      setWpStatus(res.data);
    } catch { /* ignore */ }
  };

  const loadArticle = async () => {
    try {
      const res = await articlesApi.get(id);
      setArticle(res.data);
      setEditTitle(res.data.title);
      setEditContent(res.data.content_html);
      setEditMeta(res.data.meta_description);
    } catch {
      toast.error("Error al cargar el artículo");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await articlesApi.update(id, {
        title: editTitle,
        content_html: editContent,
        meta_description: editMeta,
      });
      setArticle(res.data);
      setEditMode(false);
      toast.success("Artículo guardado");
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    try {
      const res = await articlesApi.update(id, { status: article.status === "published" ? "draft" : "published" });
      setArticle(res.data);
      toast.success(res.data.status === "published" ? "Artículo publicado" : "Movido a borrador");
    } catch {
      toast.error("Error al actualizar estado");
    }
  };

  const handleDelete = async () => {
    try {
      await articlesApi.delete(id);
      toast.success("Artículo eliminado");
      navigate("/dashboard");
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const handleGenerateImage = async () => {
    setImgDialog(true);
  };

  const labelFor = (arr, id) => arr.find((x) => x.id === id)?.label || id;

  const submitImageGen = async () => {
    setImgDialog(false);
    setGeneratingImage(true);
    try {
      const payload = {
        engine: imgForm.engine || undefined,
        style: imgForm.style || undefined,
        secondary_style: imgForm.secondary_style || undefined,
        mood: imgForm.mood || undefined,
        custom_prompt: imgForm.custom_prompt?.slice(0, 200) || undefined,
      };
      const res = await articlesApi.generateImage(id, payload);
      setArticle((prev) => ({ ...prev, featured_image: res.data.image_base64 }));
      setImgMeta({
        engine: res.data.engine_used,
        style: res.data.style_used,
        secondary: res.data.secondary_style_used,
        mood: res.data.mood_used,
        custom: res.data.custom_prompt_used,
      });
      const engineLabel = res.data.engine_used === "nano_banana" ? "Nano Banana" : "GPT Image 1";
      toast.success(`Imagen creada · ${engineLabel} · ${res.data.style_used || ""}${res.data.mood_used ? " · " + res.data.mood_used : ""}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al generar imagen");
    } finally {
      setGeneratingImage(false);
    }
  };

  const handleCopyHTML = () => {
    navigator.clipboard.writeText(article.content_html);
    toast.success("HTML copiado al portapapeles");
  };

  const handlePublishWordPress = async () => {
    setPublishingWP(true);
    try {
      const scheduledDate = wpPublishStatus === "future" && wpScheduleDate ? wpScheduleDate : null;
      const status = wpPublishStatus === "future" ? "future" : wpPublishStatus;
      const cats = wpCategories.split(",").map(s => s.trim()).filter(Boolean);
      const tags = wpTags.split(",").map(s => s.trim()).filter(Boolean);
      const res = await articlesApi.publishWordPress(id, status, scheduledDate, cats, tags);
      setArticle((prev) => ({
        ...prev,
        wp_post_id: res.data.wp_post_id,
        wp_link: res.data.wp_link,
        wp_status: res.data.wp_status,
        wp_categories: res.data.categories || prev.wp_categories,
        wp_tags: res.data.tags || prev.wp_tags,
        status: wpPublishStatus === "publish" ? "published" : (wpPublishStatus === "future" ? "scheduled" : prev.status),
      }));
      const label = wpPublishStatus === "future" ? "programado" : (wpPublishStatus === "publish" ? "publicado" : "borrador");
      toast.success(`Artículo enviado a WordPress como ${label}`);
      if (res.data.featured_image_warning) {
        toast.warning(`Imagen destacada no se subió: ${res.data.featured_image_warning}`, { duration: 10000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al publicar en WordPress");
    } finally {
      setPublishingWP(false);
    }
  };

  const handlePublishSocial = async () => {
    if (!article?.social_posts) {
      toast.error("Genera primero los posts para redes sociales desde el panel 'Redes Sociales'");
      return;
    }
    setPublishingSocial(true);
    try {
      const res = await articlesApi.publishSocial(id);
      const nets = res.data?.networks || [];
      toast.success(`Publicacion enviada a ${nets.length} red${nets.length === 1 ? "" : "es"}: ${nets.join(", ")}`);
      setArticle((prev) => ({ ...prev, social_published_at: new Date().toISOString() }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al publicar en redes");
    } finally {
      setPublishingSocial(false);
    }
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Máximo 5MB"); return; }
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await articlesApi.uploadImage(id, formData);
      setArticle((prev) => ({ ...prev, featured_image: res.data.image_base64 }));
      toast.success("Imagen subida correctamente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir imagen");
    }
  };

  const handleDownloadPDF = () => {
    if (!article) return;
    const hasImage = !!article.featured_image;
    const imageHtml = hasImage
      ? `<div style="text-align:center;margin-bottom:24px"><img id="hero-img" src="data:image/png;base64,${article.featured_image}" style="max-width:100%;max-height:400px;border-radius:8px" /></div>`
      : "";
    const content = `
      <html><head><meta charset="utf-8"><title>${article.title}</title>
      <style>body{font-family:Georgia,serif;max-width:800px;margin:40px auto;padding:20px;color:#222;line-height:1.7}
      h1{font-size:28px;margin-bottom:8px}h2{font-size:22px;margin-top:28px;border-bottom:1px solid #ddd;padding-bottom:6px}
      .meta{color:#666;font-size:14px;margin-bottom:24px}blockquote{border-left:3px solid #ccc;margin:16px 0;padding:8px 16px;color:#555}
      .faq{background:#f7f7f7;padding:16px;border-radius:8px;margin-top:24px}
      .faq h3{font-size:16px;margin:12px 0 4px}
      @media print{body{margin:20px}}</style></head><body>
      <h1>${article.title}</h1>
      <div class="meta">${article.meta_description || ""}<br>${article.reading_time || 0} min lectura</div>
      ${imageHtml}
      ${stripLeadingH1(article.content_html || "")}
      ${article.faq && article.faq.length > 0 ? `<div class="faq"><h2>Preguntas Frecuentes</h2>${article.faq.map(f => `<h3>${(f && (f.question || f.pregunta)) || ""}</h3><p>${(f && (f.answer || f.respuesta)) || ""}</p>`).join("")}</div>` : ""}
      ${article.fuentes && article.fuentes.length > 0 ? `<div class="sources"><h2>Fuentes</h2><ol>${article.fuentes.map(raw => {
        const u = typeof raw === "string" ? raw : (raw && (raw.url || raw.href || raw.link)) || "";
        const lab = typeof raw === "string" ? raw : (raw && (raw.title || raw.name || raw.url)) || String(raw || "");
        return u ? `<li><a href="${u}" target="_blank" rel="noopener nofollow">${lab}</a></li>` : `<li>${lab}</li>`;
      }).join("")}</ol></div>` : ""}
      <script>
        (function(){
          var img = document.getElementById('hero-img');
          function go(){ try { window.focus(); window.print(); } catch(e){} }
          if (!img) { window.addEventListener('load', go); return; }
          if (img.complete && img.naturalWidth > 0) { setTimeout(go, 300); }
          else {
            img.addEventListener('load', function(){ setTimeout(go, 300); });
            img.addEventListener('error', function(){ setTimeout(go, 300); });
            // Hard fallback after 6s in case decode is slow
            setTimeout(go, 6000);
          }
        })();
      </script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) {
      toast.error("Activa las ventanas emergentes para descargar el PDF");
      return;
    }
    w.document.open();
    w.document.write(content);
    w.document.close();
  };

  const handleDownloadImage = () => {
    if (!article?.featured_image) return;
    const byteChars = atob(article.featured_image);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
    const blob = new Blob([new Uint8Array(byteNumbers)], { type: "image/png" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(article.title || "imagen").replace(/[^a-zA-Z0-9]/g, "_")}.png`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Imagen descargada");
  };

  const handleGenerateSocial = async () => {
    const selectedNets = Object.entries(socialNetworks).filter(([, v]) => v).map(([k]) => k);
    if (selectedNets.length === 0) {
      toast.error("Selecciona al menos una red social");
      return;
    }
    setGeneratingSocial(true);
    try {
      const res = await articlesApi.generateSocial(id, selectedNets);
      const jobId = res.data.job_id;
      // Poll for completion
      for (let i = 0; i < 40; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const status = await articlesApi.socialStatus(jobId);
        if (status.data.status === "completed") {
          setArticle((prev) => ({ ...prev, social_posts: status.data.social_posts }));
          toast.success("Contenido social generado correctamente");
          return;
        }
        if (status.data.status === "error") {
          toast.error(status.data.detail || "Error al generar contenido social");
          return;
        }
      }
      toast.error("Timeout: la generación tardó demasiado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al generar contenido social");
    } finally {
      setGeneratingSocial(false);
    }
  };

  const handleDownload = async (format) => {
    try {
      const res = await articlesApi.download(id, format);
      const { content, filename, type } = res.data;
      const blob = new Blob([content], { type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Descargado como ${format.toUpperCase()}`);
    } catch {
      toast.error("Error al descargar");
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: "#0B1622" }}>
        <Navbar />
        <div className="max-w-5xl mx-auto px-6 py-12">
          <div className="animate-pulse space-y-4">
            <div className="h-8 rounded w-2/3" style={{ background: "#0B1622" }} />
            <div className="h-4 rounded w-1/3" style={{ background: "#0B1622" }} />
            <div className="h-64 rounded" style={{ background: "#0B1622" }} />
          </div>
        </div>
      </div>
    );
  }

  if (!article) return null;

  const cls = classificationConfig[article.classification] || classificationConfig.informativo;
  const ClsIcon = cls.icon;

  return (
    <div data-testid="article-detail-page" className="min-h-screen" style={{ background: "#0B1622" }}>
      <Navbar />

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Top bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <button
            data-testid="back-to-dashboard"
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-1.5 text-sm transition-colors duration-200"
            style={{ color: "#94A3B8", fontFamily: "'Outfit', sans-serif" }}
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al panel
          </button>

          <div className="flex flex-wrap items-center gap-2">
            {editMode ? (
              <>
                <Button
                  data-testid="cancel-edit-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => { setEditMode(false); setEditTitle(article.title); setEditContent(article.content_html); setEditMeta(article.meta_description); }}
                  className="text-sm"
                  style={{ borderColor: "#1E3A5F", color: "#94A3B8" }}
                >
                  Cancelar
                </Button>
                <Button
                  data-testid="save-article-btn"
                  size="sm"
                  onClick={handleSave}
                  disabled={saving}
                  className="text-sm text-white rounded-full px-5"
                  style={{ background: "#00D4FF" }}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Guardar
                </Button>
              </>
            ) : (
              <>
                <Button
                  data-testid="edit-article-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => setEditMode(true)}
                  className="text-sm"
                  style={{ borderColor: "#1E3A5F", color: "#E2E8F0" }}
                >
                  <Pencil className="w-3.5 h-3.5 mr-1" />
                  Editar
                </Button>
                <Button
                  data-testid="publish-article-btn"
                  size="sm"
                  onClick={handlePublish}
                  className="text-sm text-white rounded-full px-5"
                  style={{ background: article.status === "published" ? "#FBBF24" : "#4ADE80" }}
                  title="Cambia el estado interno del artículo en La Forja (no publica en WordPress)"
                >
                  <Send className="w-3.5 h-3.5 mr-1" />
                  {article.status === "published" ? "Marcar como borrador" : "Marcar como publicado"}
                </Button>
                {wpStatus?.connected ? (
                  <div className="flex flex-col gap-2 w-full sm:w-auto">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <select
                        data-testid="wp-status-select"
                        value={wpPublishStatus}
                        onChange={(e) => setWpPublishStatus(e.target.value)}
                        className="h-9 text-xs rounded-lg border px-2 cursor-pointer"
                        style={{ background: "#0F2035", borderColor: "#1E3A5F", color: "#E2E8F0", outline: "none" }}
                      >
                        <option value="draft">Borrador</option>
                        <option value="publish">Publicado</option>
                        <option value="future">Programar</option>
                      </select>
                      {wpPublishStatus === "future" && (
                        <input
                          data-testid="wp-schedule-date"
                          type="datetime-local"
                          value={wpScheduleDate}
                          onChange={(e) => setWpScheduleDate(e.target.value)}
                          min={new Date().toISOString().slice(0, 16)}
                          className="h-9 text-xs rounded-lg border px-2"
                          style={{ background: "#0F2035", borderColor: "#1E3A5F", color: "#E2E8F0", outline: "none", colorScheme: "dark" }}
                        />
                      )}
                      <button
                        data-testid="wp-advanced-toggle"
                        onClick={() => setShowWpAdvanced(!showWpAdvanced)}
                        className="h-9 text-xs rounded-lg border px-3 flex items-center gap-1 transition-colors"
                        style={{
                          background: showWpAdvanced || wpCategories || wpTags ? "rgba(0,212,255,0.08)" : "#0F2035",
                          borderColor: showWpAdvanced || wpCategories || wpTags ? "rgba(0,212,255,0.3)" : "#1E3A5F",
                          color: showWpAdvanced || wpCategories || wpTags ? "#00D4FF" : "#94A3B8",
                        }}
                      >
                        <Tag className="w-3.5 h-3.5" />
                        Categorías y etiquetas
                        {(wpCategories || wpTags) && <span className="ml-1 px-1.5 rounded-full text-[10px]" style={{ background: "#00D4FF", color: "#0B1622" }}>•</span>}
                      </button>
                      <Button
                        data-testid="publish-wordpress-btn"
                        size="sm"
                        onClick={handlePublishWordPress}
                        disabled={publishingWP || (wpPublishStatus === "future" && !wpScheduleDate)}
                        className="text-sm text-white rounded-full px-5"
                        style={{ background: "#21759b" }}
                      >
                        {publishingWP ? (
                          <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                        ) : (
                          <Globe className="w-3.5 h-3.5 mr-1" />
                        )}
                        {publishingWP ? "Enviando..." : "WordPress"}
                      </Button>
                    </div>
                    {showWpAdvanced && (
                      <div data-testid="wp-advanced-panel" className="rounded-lg border p-3 space-y-2"
                        style={{ background: "#0B1622", borderColor: "#1E3A5F" }}>
                        <div>
                          <label className="text-xs block mb-1" style={{ color: "#94A3B8" }}>
                            Categorías <span style={{ color: "#64748B" }}>(separadas por comas — se crean si no existen)</span>
                          </label>
                          <input
                            data-testid="wp-categories-input"
                            type="text"
                            value={wpCategories}
                            onChange={(e) => setWpCategories(e.target.value)}
                            placeholder="Marketing Digital, SEO, IA"
                            className="w-full h-9 rounded-md border px-3 text-sm"
                            style={{ background: "#0F2035", borderColor: "#1E3A5F", color: "#E2E8F0", outline: "none" }}
                          />
                        </div>
                        <div>
                          <label className="text-xs block mb-1" style={{ color: "#94A3B8" }}>
                            Etiquetas <span style={{ color: "#64748B" }}>(separadas por comas — se crean si no existen)</span>
                          </label>
                          <input
                            data-testid="wp-tags-input"
                            type="text"
                            value={wpTags}
                            onChange={(e) => setWpTags(e.target.value)}
                            placeholder="claude, gpt, automatización"
                            className="w-full h-9 rounded-md border px-3 text-sm"
                            style={{ background: "#0F2035", borderColor: "#1E3A5F", color: "#E2E8F0", outline: "none" }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Button
                    data-testid="wp-not-connected-btn"
                    size="sm"
                    onClick={() => navigate("/settings")}
                    variant="outline"
                    className="text-sm rounded-full px-4"
                    style={{ borderColor: "#FBBF24", color: "#FBBF24" }}
                    title="Configura tu WordPress en Ajustes para publicar"
                  >
                    <Globe className="w-3.5 h-3.5 mr-1" />
                    Conectar WordPress
                  </Button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" style={{ borderColor: "#1E3A5F", color: "#94A3B8" }}>
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
                    <DropdownMenuItem data-testid="copy-html-btn" onClick={handleCopyHTML} style={{ color: "#E2E8F0" }}>
                      <Copy className="w-3.5 h-3.5 mr-2" /> Copiar HTML
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="download-html-btn" onClick={() => handleDownload("html")} style={{ color: "#E2E8F0" }}>
                      <FileCode className="w-3.5 h-3.5 mr-2" /> Descargar HTML
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="download-text-btn" onClick={() => handleDownload("text")} style={{ color: "#E2E8F0" }}>
                      <FileText className="w-3.5 h-3.5 mr-2" /> Descargar Texto
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="download-pdf-btn" onClick={handleDownloadPDF} style={{ color: "#E2E8F0" }}>
                      <FileDown className="w-3.5 h-3.5 mr-2" /> Descargar PDF
                    </DropdownMenuItem>
                    {article.featured_image && (
                      <DropdownMenuItem data-testid="download-image-btn" onClick={handleDownloadImage} style={{ color: "#E2E8F0" }}>
                        <Download className="w-3.5 h-3.5 mr-2" /> Descargar Imagen
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem data-testid="delete-article-trigger" onClick={() => setDeleteDialog(true)} style={{ color: "#ef4444" }}>
                      <Trash2 className="w-3.5 h-3.5 mr-2" /> Eliminar
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="rounded-xl border p-8" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
              {/* Featured Image */}
              {article.featured_image ? (
                <div className="mb-6 rounded-lg overflow-hidden relative group" style={{ maxHeight: "360px" }}>
                  <img
                    data-testid="featured-image"
                    src={`data:image/png;base64,${article.featured_image}`}
                    alt={article.title}
                    className="w-full object-cover"
                    style={{ maxHeight: "360px" }}
                  />
                  {imgMeta && (
                    <div
                      data-testid="img-meta-badge"
                      className="absolute top-2 left-2 px-2.5 py-1 rounded-md text-xs flex items-center gap-1.5"
                      style={{ background: "rgba(11,22,34,0.85)", color: "#00D4FF", border: "1px solid rgba(0,212,255,0.3)", fontFamily: "'IBM Plex Mono', monospace" }}
                    >
                      <span>{imgMeta.engine === "nano_banana" ? "Nano Banana" : "GPT Image 1"}</span>
                      {imgMeta.style && <span style={{ color: "#94A3B8" }}>· {imgMeta.style}</span>}
                      {imgMeta.mood && <span style={{ color: "#94A3B8" }}>· {imgMeta.mood}</span>}
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                    <button
                      data-testid="regenerate-image-btn"
                      onClick={handleGenerateImage}
                      disabled={generatingImage}
                      className="px-4 py-2 rounded-full text-xs font-medium"
                      style={{ background: "#00D4FF", color: "#0B1622" }}
                    >
                      {generatingImage ? "Generando..." : "Regenerar con IA"}
                    </button>
                    <button
                      data-testid="reupload-image-btn"
                      onClick={() => imageUploadRef.current?.click()}
                      className="px-4 py-2 rounded-full text-xs font-medium"
                      style={{ background: "#F97316", color: "white" }}
                    >
                      Subir otra
                    </button>
                    <button
                      data-testid="download-featured-image-btn"
                      onClick={handleDownloadImage}
                      className="px-4 py-2 rounded-full text-xs font-medium"
                      style={{ background: "#4ADE80", color: "#0B1622" }}
                    >
                      <Download className="w-3.5 h-3.5 inline mr-1" />Descargar
                    </button>
                  </div>
                  <input ref={imageUploadRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleUploadImage} />
                </div>
              ) : (
                <div className="mb-6 rounded-lg border-2 border-dashed p-8 flex flex-col items-center gap-3" style={{ borderColor: "#1E3A5F" }}>
                  {generatingImage ? (
                    <>
                      <Loader2 className="w-8 h-8 animate-spin" style={{ color: "#00D4FF" }} />
                      <span className="text-sm" style={{ color: "#00D4FF", fontFamily: "'IBM Plex Mono', monospace" }}>
                        Generando imagen con IA...
                      </span>
                    </>
                  ) : (
                    <>
                      <ImagePlus className="w-8 h-8" style={{ color: "#94A3B8" }} />
                      <span className="text-sm" style={{ color: "#94A3B8" }}>Imagen destacada</span>
                      <div className="flex gap-3">
                        <button
                          data-testid="generate-image-btn"
                          onClick={handleGenerateImage}
                          className="px-4 py-2 rounded-full text-xs font-medium transition-colors"
                          style={{ background: "rgba(0,212,255,0.1)", color: "#00D4FF", border: "1px solid rgba(0,212,255,0.3)" }}
                        >
                          <ImagePlus className="w-3.5 h-3.5 inline mr-1.5" />Generar con IA
                        </button>
                        <button
                          data-testid="upload-image-btn"
                          onClick={() => imageUploadRef.current?.click()}
                          className="px-4 py-2 rounded-full text-xs font-medium transition-colors"
                          style={{ background: "rgba(249,115,22,0.1)", color: "#F97316", border: "1px solid rgba(249,115,22,0.3)" }}
                        >
                          <ImagePlus className="w-3.5 h-3.5 inline mr-1.5" />Subir imagen
                        </button>
                      </div>
                      <input ref={imageUploadRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleUploadImage} />
                    </>
                  )}
                </div>
              )}

              {/* Title */}
              {editMode ? (
                <Input
                  data-testid="edit-title-input"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="text-2xl font-bold mb-4 h-auto py-2"
                  style={{ fontFamily: "'Space Grotesk', serif", background: "#0B1622", borderColor: "#1E3A5F", color: "#E2E8F0" }}
                />
              ) : (
                <h1
                  data-testid="article-title"
                  className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 tracking-tight"
                  style={{ fontFamily: "'Space Grotesk', serif", color: "#E2E8F0" }}
                >
                  {article.title}
                </h1>
              )}

              {/* Meta */}
              <div className="flex flex-wrap items-center gap-4 mb-6">
                <Badge
                  className="text-xs px-2.5 py-0.5 rounded-full border"
                  style={{ background: cls.bg, color: cls.color, borderColor: `${cls.color}33` }}
                >
                  <ClsIcon className="w-3 h-3 mr-1" />
                  {cls.label}
                </Badge>
                <span className="flex items-center gap-1 text-xs" style={{ color: "#94A3B8", fontFamily: "'IBM Plex Mono', monospace" }}>
                  <Clock className="w-3.5 h-3.5" /> {article.reading_time} min lectura
                </span>
                <span className="flex items-center gap-1 text-xs" style={{ color: "#94A3B8", fontFamily: "'IBM Plex Mono', monospace" }}>
                  <Calendar className="w-3.5 h-3.5" /> {formatDate(article.created_at)}
                </span>
              </div>

              <Separator className="mb-6" style={{ background: "#1E3A5F" }} />

              {/* Content */}
              {editMode ? (
                <Textarea
                  data-testid="edit-content-textarea"
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  rows={25}
                  className="text-sm font-mono resize-none"
                  style={{ background: "#0B1622", borderColor: "#1E3A5F", color: "#E2E8F0" }}
                />
              ) : (
                <div
                  data-testid="article-content"
                  className="article-body"
                  style={{ fontFamily: "'Crimson Pro', serif", color: "#E2E8F0", lineHeight: "1.8" }}
                >
                  <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                    {stripLeadingH1(article.content_html)}
                  </ReactMarkdown>
                </div>
              )}

              {/* FAQ Section */}
              {article.faq && article.faq.length > 0 && (
                <div className="mt-8 pt-8" style={{ borderTop: "1px solid #1E3A5F" }}>
                  <h2
                    className="text-xl font-bold mb-4 flex items-center gap-2"
                    style={{ fontFamily: "'Space Grotesk', serif", color: "#E2E8F0" }}
                  >
                    <HelpCircle className="w-5 h-5" style={{ color: "#00D4FF" }} />
                    Preguntas Frecuentes
                  </h2>
                  <div className="space-y-4">
                    {(Array.isArray(article.faq) ? article.faq : []).map((item, i) => {
                      // Defensive: faq items can be {question, answer} or {pregunta, respuesta} or strings
                      const q = (item && typeof item === "object"
                        ? (item.question || item.pregunta || item.q || item.title || "")
                        : String(item || "")) || "";
                      const a = (item && typeof item === "object"
                        ? (item.answer || item.respuesta || item.a || item.text || "")
                        : "") || "";
                      if (!q && !a) return null;
                      return (
                        <div
                          key={i}
                          data-testid={`faq-item-${i}`}
                          className="rounded-lg p-4 border"
                          style={{ background: "#0B1622", borderColor: "#1E3A5F" }}
                        >
                          {q && (
                            <h4 className="text-sm font-semibold mb-2" style={{ color: "#E2E8F0", fontFamily: "'Outfit', sans-serif" }}>
                              {q}
                            </h4>
                          )}
                          {a && (
                            <p className="text-sm" style={{ color: "#94A3B8", fontFamily: "'Crimson Pro', serif", lineHeight: "1.7" }}>
                              {a}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Fuentes Section */}
              {article.fuentes && article.fuentes.length > 0 && (
                <div data-testid="article-sources" className="mt-8 pt-8" style={{ borderTop: "1px solid #1E3A5F" }}>
                  <h2
                    className="text-xl font-bold mb-4 flex items-center gap-2"
                    style={{ fontFamily: "'Space Grotesk', serif", color: "#E2E8F0" }}
                  >
                    <ExternalLink className="w-5 h-5" style={{ color: "#00D4FF" }} />
                    Fuentes
                  </h2>
                  <ol className="space-y-2 list-decimal pl-5">
                    {article.fuentes.map((raw, i) => {
                      // Defensive normalization: Claude sometimes returns objects with {url, title} or other shapes
                      const url = typeof raw === "string"
                        ? raw
                        : (raw && typeof raw === "object" && (raw.url || raw.href || raw.link)) || "";
                      const label = typeof raw === "string"
                        ? raw
                        : (raw && typeof raw === "object" && (raw.title || raw.name || raw.url || raw.href)) || String(raw || "");
                      if (!url && !label) return null;
                      return (
                        <li key={i} data-testid={`source-item-${i}`} style={{ color: "#94A3B8" }}>
                          {url ? (
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer nofollow"
                              className="text-sm break-all hover:underline"
                              style={{ color: "#00D4FF", fontFamily: "'Outfit', sans-serif" }}
                            >
                              {label}
                            </a>
                          ) : (
                            <span className="text-sm" style={{ color: "#94A3B8" }}>{label}</span>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                </div>
              )}

              {/* HIDALGO disclaimer — Nivel 1 */}
              <div data-testid="hidalgo-disclaimer" className="mt-10 pt-6 flex items-start gap-3" style={{ borderTop: "1px solid #1E3A5F" }}>
                <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-lg"
                  style={{ background: "rgba(0,212,255,0.08)", border: "1px solid rgba(0,212,255,0.2)" }}>
                  🛡️
                </div>
                <div className="flex-1">
                  <p className="text-xs" style={{ color: "#64748B", lineHeight: 1.6, fontStyle: "italic" }}>
                    <strong style={{ color: "#94A3B8" }}>HIDALGO:</strong> Cervantes tardó años en escribir el Quijote. HIDALGO lo hace en segundos, así que revísalo antes de publicar.
                  </p>
                </div>
              </div>
            </div>

            {/* Social Media Section */}
            <div className="mt-6">
              {article.social_posts ? (
                <>
                  <SocialPreviews socialPosts={article.social_posts} articleId={article.id} wpLink={article.wp_link} activeNetworks={socialNetworks} />
                  <div className="mt-6">
                    <SocialImagesGenerator articleId={article.id} />
                  </div>
                </>
              ) : (
                <div className="rounded-xl border p-8" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
                  <div className="text-center mb-6">
                    <Share2 className="w-10 h-10 mx-auto mb-3" style={{ color: "#94A3B8" }} />
                    <h3 className="text-base font-semibold mb-1" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>
                      Contenido para Redes Sociales
                    </h3>
                    <p className="text-sm mb-4" style={{ color: "#94A3B8" }}>
                      Selecciona las redes y genera posts optimizados
                    </p>
                  </div>
                  {/* Network toggles */}
                  <div className="flex justify-center gap-3 mb-6">
                    {[
                      { id: "linkedin", label: "LinkedIn", color: "#0A66C2" },
                      { id: "facebook", label: "Facebook", color: "#1877F2" },
                      { id: "instagram", label: "Instagram", color: "#E1306C" },
                      { id: "x", label: "X", color: "#94A3B8" },
                    ].map((net) => (
                      <button key={net.id} data-testid={`social-toggle-${net.id}`}
                        onClick={() => setSocialNetworks((prev) => ({ ...prev, [net.id]: !prev[net.id] }))}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm transition-all duration-200"
                        style={{
                          background: socialNetworks[net.id] ? `${net.color}10` : "transparent",
                          borderColor: socialNetworks[net.id] ? `${net.color}40` : "#1E3A5F",
                          color: socialNetworks[net.id] ? "#E2E8F0" : "#64748B",
                        }}>
                        <div className="w-2 h-2 rounded-full" style={{ background: socialNetworks[net.id] ? net.color : "#475569" }} />
                        {net.label}
                      </button>
                    ))}
                  </div>
                  <div className="flex justify-center">
                    <Button
                      data-testid="generate-social-btn"
                      onClick={handleGenerateSocial}
                      disabled={generatingSocial || !Object.values(socialNetworks).some(Boolean)}
                      className="rounded-full px-6 h-10 text-sm font-medium"
                      style={{ background: generatingSocial ? "rgba(0,212,255,0.15)" : "linear-gradient(135deg, #00B4D8, #00D4FF)", color: generatingSocial ? "#00D4FF" : "#0B1622" }}
                    >
                      {generatingSocial ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generando contenido social...</>
                      ) : (
                        <><Share2 className="w-4 h-4 mr-2" />Generar Contenido Social</>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-center mt-3" style={{ color: "#64748B" }}>
                    Configura tus redes por defecto en <a href="/settings" style={{ color: "#00D4FF" }}>Ajustes</a>
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            {/* SEO Card */}
            <div className="rounded-xl border p-5" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
              <h3
                className="text-xs uppercase tracking-wider mb-4 flex items-center gap-2"
                style={{ color: "#94A3B8", fontFamily: "'IBM Plex Mono', monospace" }}
              >
                <Search className="w-3.5 h-3.5" />
                SEO
              </h3>

              <div className="space-y-3">
                <div>
                  <span className="text-xs block mb-1" style={{ color: "#94A3B8" }}>Meta descripción</span>
                  {editMode ? (
                    <Textarea
                      data-testid="edit-meta-textarea"
                      value={editMeta}
                      onChange={(e) => setEditMeta(e.target.value)}
                      rows={3}
                      className="text-xs resize-none"
                      style={{ background: "#0B1622", borderColor: "#1E3A5F", color: "#E2E8F0" }}
                    />
                  ) : (
                    <p className="text-xs" style={{ color: "#E2E8F0", lineHeight: "1.6" }}>
                      {article.meta_description}
                    </p>
                  )}
                  <span
                    className="text-xs mt-1 block"
                    style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      color: (article.meta_description?.length || 0) > 160 ? "#ef4444" : "#4ADE80",
                    }}
                  >
                    {article.meta_description?.length || 0}/160
                  </span>
                </div>
              </div>
            </div>

            {/* Keywords Card */}
            <div className="rounded-xl border p-5" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
              <h3
                className="text-xs uppercase tracking-wider mb-4 flex items-center gap-2"
                style={{ color: "#94A3B8", fontFamily: "'IBM Plex Mono', monospace" }}
              >
                <Tag className="w-3.5 h-3.5" />
                Keywords
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {(article.keywords || []).map((kw, i) => (
                  <span
                    key={i}
                    className="text-xs px-2 py-1 rounded border"
                    style={{ background: "rgba(0,212,255,0.08)", color: "#00D4FF", borderColor: "rgba(0,212,255,0.2)", fontFamily: "'IBM Plex Mono', monospace" }}
                  >
                    {kw}
                  </span>
                ))}
              </div>
            </div>

            {/* Classification Card */}
            <div className="rounded-xl border p-5" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
              <h3
                className="text-xs uppercase tracking-wider mb-4"
                style={{ color: "#94A3B8", fontFamily: "'IBM Plex Mono', monospace" }}
              >
                Clasificación
              </h3>
              <div
                className="rounded-lg p-3 border flex items-center gap-3"
                style={{ background: cls.bg, borderColor: `${cls.color}33` }}
              >
                <ClsIcon className="w-5 h-5" style={{ color: cls.color }} />
                <div>
                  <div className="text-sm font-medium" style={{ color: cls.color }}>{cls.label}</div>
                  <div className="text-xs" style={{ color: "#94A3B8" }}>{cls.desc}</div>
                </div>
              </div>
            </div>

            {/* Status Card */}
            <div className="rounded-xl border p-5" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
              <h3
                className="text-xs uppercase tracking-wider mb-4"
                style={{ color: "#94A3B8", fontFamily: "'IBM Plex Mono', monospace" }}
              >
                Estado
              </h3>
              <Badge
                className="text-xs px-3 py-1 rounded-full"
                style={{
                  background: article.status === "published" ? "rgba(74,222,128,0.1)" : "rgba(251,191,36,0.1)",
                  color: article.status === "published" ? "#4ADE80" : "#FBBF24",
                  borderColor: article.status === "published" ? "#4ADE8033" : "#FBBF2433",
                }}
              >
                {article.status === "published" ? "Publicado" : "Borrador"}
              </Badge>
            </div>

            {/* WordPress Card */}
            {wpStatus?.connected && (
              <div className="rounded-xl border p-5" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
                <h3
                  className="text-xs uppercase tracking-wider mb-4 flex items-center gap-2"
                  style={{ color: "#94A3B8", fontFamily: "'IBM Plex Mono', monospace" }}
                >
                  <Globe className="w-3.5 h-3.5" />
                  WordPress
                </h3>
                {article.wp_post_id ? (
                  <div className="space-y-2">
                    <Badge
                      className="text-xs px-3 py-1 rounded-full"
                      style={{ background: "rgba(33,117,155,0.1)", color: "#21759b", borderColor: "#21759b33" }}
                    >
                      Enviado a WP
                    </Badge>
                    {article.wp_link && (
                      <a
                        data-testid="wp-link"
                        href={article.wp_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs mt-2 transition-colors duration-200"
                        style={{ color: "#21759b" }}
                      >
                        <ExternalLink className="w-3 h-3" />
                        Ver en WordPress
                      </a>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <select
                      data-testid="sidebar-wp-status-select"
                      value={wpPublishStatus}
                      onChange={(e) => setWpPublishStatus(e.target.value)}
                      className="w-full h-8 text-xs rounded-lg border px-2"
                      style={{ background: "#0B1622", borderColor: "#1E3A5F", color: "#E2E8F0", outline: "none" }}
                    >
                      <option value="draft">Borrador</option>
                      <option value="publish">Publicado</option>
                    </select>
                    <Button
                      data-testid="sidebar-publish-wp-btn"
                      size="sm"
                      onClick={handlePublishWordPress}
                      disabled={publishingWP}
                      className="w-full text-xs text-white"
                      style={{ background: "#21759b" }}
                    >
                      {publishingWP ? (
                        <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                      ) : (
                        <Globe className="w-3.5 h-3.5 mr-1" />
                      )}
                      {publishingWP ? "Enviando..." : "Publicar en WordPress"}
                    </Button>
                  </div>
                )}
                <p className="text-xs mt-2" style={{ color: "#94A3B8" }}>
                  Conectado a: {wpStatus.wp_user}
                </p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
          <DialogHeader>
            <DialogTitle style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk', serif" }}>Eliminar artículo</DialogTitle>
            <DialogDescription style={{ color: "#94A3B8" }}>
              Esta acción no se puede deshacer. El artículo será eliminado permanentemente.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              data-testid="cancel-delete-btn"
              variant="outline"
              onClick={() => setDeleteDialog(false)}
              style={{ borderColor: "#1E3A5F", color: "#94A3B8" }}
            >
              Cancelar
            </Button>
            <Button
              data-testid="confirm-delete-btn"
              onClick={handleDelete}
              style={{ background: "#ef4444", color: "white" }}
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Generation Dialog — per-article overrides */}
      <Dialog open={imgDialog} onOpenChange={setImgDialog}>
        <DialogContent style={{ background: "#0F2035", borderColor: "#1E3A5F", maxWidth: "560px" }}>
          <DialogHeader>
            <DialogTitle style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk', serif" }}>Configurar imagen destacada</DialogTitle>
            <DialogDescription style={{ color: "#94A3B8" }}>
              Los valores vienen pre-cargados de tu perfil. Puedes sobrescribirlos solo para este artículo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs block mb-1.5" style={{ color: "#94A3B8" }}>Motor IA</label>
                <select
                  data-testid="img-dialog-engine"
                  value={imgForm.engine}
                  onChange={(e) => setImgForm({ ...imgForm, engine: e.target.value })}
                  className="w-full h-9 px-2 rounded-lg text-xs border"
                  style={{ background: "#0B1622", borderColor: "#1E3A5F", color: "#E2E8F0" }}
                >
                  <option value="openai">GPT Image 1 — Fotorrealista</option>
                  <option value="nano_banana">Nano Banana — Creativo</option>
                </select>
              </div>
              <div>
                <label className="text-xs block mb-1.5" style={{ color: "#94A3B8" }}>Estilo principal</label>
                <select
                  data-testid="img-dialog-style"
                  value={imgForm.style}
                  onChange={(e) => setImgForm({ ...imgForm, style: e.target.value })}
                  className="w-full h-9 px-2 rounded-lg text-xs border"
                  style={{ background: "#0B1622", borderColor: "#1E3A5F", color: "#E2E8F0" }}
                >
                  {[
                    ["fotorrealista","Fotorrealista"],["3d","3D"],["ilustracion","Ilustracion"],["minimalista","Minimalista"],
                    ["corporativo","Corporativo"],["artistico","Artistico"],["geometrico","Geometrico"],["infografico","Infografico"],
                    ["editorial","Editorial"],["cinematografico","Cinematografico"],["comic","Comic"],["pixar","Pixar / 3D expresivo"],
                    ["manga","Manga"],["acuarela","Acuarela"],["vintage","Vintage / Retro"],["street_art","Street Art"],
                    ["scifi","Sci-Fi / Futurista"],["flat_design","Flat Design"],["organico","Organico / Natural"],["dark_dramatico","Dark / Dramatico"],
                  ].map(([id, label]) => (<option key={id} value={id}>{label}</option>))}
                </select>
              </div>
              <div>
                <label className="text-xs block mb-1.5" style={{ color: "#94A3B8" }}>Estilo secundario (opcional)</label>
                <select
                  data-testid="img-dialog-secondary"
                  value={imgForm.secondary_style}
                  onChange={(e) => setImgForm({ ...imgForm, secondary_style: e.target.value })}
                  className="w-full h-9 px-2 rounded-lg text-xs border"
                  style={{ background: "#0B1622", borderColor: "#1E3A5F", color: "#E2E8F0" }}
                >
                  <option value="">— Ninguno —</option>
                  {[
                    ["fotorrealista","Fotorrealista"],["3d","3D"],["ilustracion","Ilustracion"],["minimalista","Minimalista"],
                    ["corporativo","Corporativo"],["artistico","Artistico"],["geometrico","Geometrico"],["infografico","Infografico"],
                    ["editorial","Editorial"],["cinematografico","Cinematografico"],["comic","Comic"],["pixar","Pixar / 3D expresivo"],
                    ["manga","Manga"],["acuarela","Acuarela"],["vintage","Vintage / Retro"],["street_art","Street Art"],
                    ["scifi","Sci-Fi / Futurista"],["flat_design","Flat Design"],["organico","Organico / Natural"],["dark_dramatico","Dark / Dramatico"],
                  ].filter(([id]) => id !== imgForm.style).map(([id, label]) => (<option key={id} value={id}>{label}</option>))}
                </select>
              </div>
              <div>
                <label className="text-xs block mb-1.5" style={{ color: "#94A3B8" }}>Ambiente (Mood)</label>
                <select
                  data-testid="img-dialog-mood"
                  value={imgForm.mood}
                  onChange={(e) => setImgForm({ ...imgForm, mood: e.target.value })}
                  className="w-full h-9 px-2 rounded-lg text-xs border"
                  style={{ background: "#0B1622", borderColor: "#1E3A5F", color: "#E2E8F0" }}
                >
                  <option value="">— Por defecto —</option>
                  <option value="luminoso">Luminoso</option>
                  <option value="oscuro">Oscuro</option>
                  <option value="neutro">Neutro</option>
                  <option value="calido">Calido</option>
                  <option value="frio">Frio</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs block mb-1.5" style={{ color: "#94A3B8" }}>Descripción adicional (opcional, máx 200)</label>
              <Textarea
                data-testid="img-dialog-custom"
                value={imgForm.custom_prompt}
                onChange={(e) => setImgForm({ ...imgForm, custom_prompt: e.target.value.slice(0, 200) })}
                placeholder="Ej: mujer y hombre mirándose, luz suave, composición central..."
                rows={2}
                maxLength={200}
                className="resize-none text-xs"
                style={{ background: "#0B1622", borderColor: "#1E3A5F", color: "#E2E8F0" }}
              />
              <div className="flex justify-end mt-1">
                <span className="text-xs font-mono" style={{ color: "#64748B" }}>{imgForm.custom_prompt.length}/200</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="img-dialog-cancel" variant="outline" onClick={() => setImgDialog(false)} style={{ borderColor: "#1E3A5F", color: "#94A3B8" }}>Cancelar</Button>
            <Button data-testid="img-dialog-generate" onClick={submitImageGen} style={{ background: "linear-gradient(135deg, #00B4D8, #00D4FF)", color: "#0B1622" }}>
              <ImagePlus className="w-4 h-4 mr-1" />
              Generar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
